<template>
    <f7-page class="new-payment-mini-view">
        <div class="title-wrapper">
            <span class="title">{{ $t("newPayment.title") }}</span>
        </div>
		<PaymentInputAmount />
		<Transition name="fade" mode="out-in">
			<div v-if="textMaxMinRangeError && minMaxTextError.length" class="minmax-error-text_wrapper">
				<p class="minmax-error-text_error">{{ minMaxTextError }}</p>
			</div>
		</Transition>
        <f7-block class="actions">
            <f7-button
                class="theme-type"
                preloader
                :disabled="submitLoading || textMaxMinRangeError"
                :loading="submitLoading"
                @click="submit"
            >{{ $t("newPayment.next") }}
            </f7-button>
        </f7-block>
    </f7-page>
</template>

<script lang="ts" setup>
import NewPaymentController from "./NewPaymentController";
import PopupCreateConfigPaymentController from "@/views/payment-create-config/NewPaymentController";
import { computed, onMounted, ref, watch } from "vue";
import { f7 } from "framework7-vue";
import ErrorsService from "@/services/errors-service/ErrorsService";
import LightPaymentPidResponseError from "@models/responses/light-payment-pid/LightPaymentPidResponseError";
import { AccountError } from "@enums/ServerErrorCodes";
import { useI18n } from "vue-i18n";
import PaymentInputAmount from "@/targets/light/components/popups/create-payment/comps/PaymentInputAmount.vue";

const {t} = useI18n({useScope: 'global'});
const viewController = NewPaymentController.getInstance();
const {
	amount,
	currency,
	agentsAmountRanges,
	minMaxRangesAmount,
	paymentService,
	routerService,
	textMaxMinRangeError
} = viewController;

const submitLoading = ref(false);

onMounted(async () => {
	if (!viewController.validateMinmax()) {
		textMaxMinRangeError.value = true;
	}
});

const minMaxTextError = computed(() => {
	const { min, max } = minMaxRangesAmount.value;
	if (!min || !max) return "";

	if (amount.value > agentsAmountRanges.value!.max!) {
		return `${t("newPayment.errors.max")} ${max} ${currency.value.abbr}`;
	}

	if (amount.value < agentsAmountRanges.value!.max!) {
		return `${t("newPayment.errors.min")} ${min} ${currency.value.abbr}`;
	}

	return "";
});

watch(amount, () => {
    textMaxMinRangeError.value = false;
});

const closePopup = () => {
    const popupEl: HTMLDivElement = document.querySelector('.new-payment-mini-popup')!;
    f7.popup.get(popupEl).close();
}

const submit = async () => {
    let isClosePopup = true;
	submitLoading.value = true;

	try {
        if (!viewController.validateMinmax()) {
            textMaxMinRangeError.value = true;
			isClosePopup = false;
        } else {
			await createPayment();
        }
    } catch (e: any) {
		handlePaymentError(e, () => { isClosePopup = false });
    } finally {
        submitLoading.value = false;
        if (isClosePopup) closePopup();
    }
}

const createPayment = async () => {
	paymentService.payment.value = await viewController.next();
	paymentService.paymentError.value = null;
}

const handlePaymentError = (e: any, cancelClosePopup: () => void) => {
	const callback = getPaymentErrorHandler(e, cancelClosePopup);
	ErrorsService.of().handle(e, callback);
}

const getPaymentErrorHandler = (e: any, cancelClosePopup: () => void): (() => void | Promise<void>) | null => {
	switch (e.code) {
		case AccountError.AgentNotFound:
			return () => handleAgentNotFoundError(e);
		case AccountError.AgentsUnavailableForAmount:
			return () => handleAgentsUnavailableForAmountError(e, cancelClosePopup);
		case AccountError.AgentsUnavailableForRangeAmount:
			return () => handleAgentsUnavailableForRangeAmountError(e);
		default:
			return null;
	}
}

const handleAgentNotFoundError = (e:any) => {
	paymentService.paymentError.value = e;
}

const handleAgentsUnavailableForAmountError = async (e: any, cancelClosePopup: () => void) => {
	paymentService.paymentError.value = e;
	viewController.updateAgentsAmountRanges();
	textMaxMinRangeError.value = true;
	cancelClosePopup();
}

const handleAgentsUnavailableForRangeAmountError = async (e: any) => {
	await PopupCreateConfigPaymentController.getInstance().init();
	await routerService.toCreatePaymentPopup(
		() => {
			paymentService.paymentError.value = new LightPaymentPidResponseError(e.code, null);
			routerService.toViewPaymentCreateAgents();
		}
	);
}
</script>

<style lang="scss">
.device-android, .device-ios {
    &.olimp, &.olimp-casino, &.olimpcasinokg {
        .new-payment-popup {
            //height: calc(var(--f7-safe-area-bottom) + 406px + 100px) !important;
            //height: calc(var(--f7-safe-area-bottom) + 406px) !important;
        }
    }
}
.new-payment-mini-popup {
	height: auto !important;
}

.new-payment-mini-view {
	position: relative;
	top: unset !important;
	bottom: 0 !important;
	height: auto;
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;

    .minmax-error-text_wrapper {
        margin: 8px 16px 0;
        @include text(12px, 400, var(--red));
        height: 12px;
    }

    .title-wrapper {
        margin: 24px 16px 0;

        .title {
            font-weight: 700;
            font-size: 22px;
            line-height: 120%;
            letter-spacing: 0.337647px;
            color: #000000;
        }
    }

    .actions {
        margin-top: calc(16px);
        margin-bottom: 16px !important;
    }

    .list {
        margin-bottom: 0;

        & + .actions {
            margin-top: 36px;
        }
    }
}
</style>
